// Помощник: вопрос словами — ответ числами из базы.
//
// Ответ несёт не только фразу, но и величины, из которых она сложена, и экран, где те же
// числа видно глазами. Показывать одну фразу нельзя: сумму, которую нечем проверить, человек
// либо перепроверяет руками (и помощник бесполезен), либо принимает на веру (и однажды
// ошибётся вместе с ним).

import { request } from '@/shared/api/client';
import type { ListResponse } from '@/shared/api/types';

export interface AssistantFact {
  title: string;
  value: string;
}

export interface AssistantAnswer {
  answer: string;
  facts: AssistantFact[];
  source: string;
  link?: string | null;
  /** Какой именно запрос выполнен. null — вопрос не понят. */
  query_code?: string | null;
  /** Фразу сложила модель, а не код. */
  by_model: boolean;
  /** Числа сверены с данными. false — фраза модели забракована, показаны только величины. */
  numbers_verified: boolean;
  note?: string | null;
}

export interface AssistantQuery {
  code: string;
  about: string;
  examples: string[];
  /**
   * Готовый текст кнопки: живая фамилия и месяц уже подставлены сервером.
   *
   * Отдельно от `examples` намеренно. Примеры — словарь ПОДБОРА на сервере, а кнопка обязана
   * быть вопросом, на который система сегодня ответит числом. Это не одно и то же: подбору
   * «сколько к выплате» понятно, а человеку он отвечает «назовите месяц и год».
   *
   * Пусто — сервер старый, показываем первый пример, как делали до 08.09.2026.
   */
  hint?: string;
}

/** Что писать на кнопке. Пусто — предлагать нечего, вопрос пропускается. */
export function подсказкаЗапроса(вид: AssistantQuery): string | null {
  return вид.hint?.trim() || вид.examples[0] || null;
}

/**
 * Спросить. `history` — прошлые вопросы этого же человека, свежий первым.
 *
 * ⚠️ Без них разговора нет: «сколько висит на Петрове» → «а у Иванова?» сервер понять не
 * сможет, потому что во второй реплике нет ни одного слова каталога. Сервер берёт из прошлых
 * вопросов ровно две вещи — месяц и фамилию, — и этого хватает на весь разговор, который у
 * помощника бывает.
 */
export function ask(question: string, history: string[] = []): Promise<AssistantAnswer> {
  return request<AssistantAnswer>('/api/v1/assistant/ask', {
    method: 'POST',
    // Пять последних: сервер больше и не читает, а длинный хвост означал бы, что месяц,
    // названный десять реплик назад, всё ещё считается уточнением.
    body: { question, history: history.slice(0, 5) },
  });
}

export function fetchAssistantQueries(): Promise<ListResponse<AssistantQuery>> {
  return request<ListResponse<AssistantQuery>>('/api/v1/assistant/queries');
}
